import { unminifyError } from '@sgme/stacktrace';
import { BlockFallback } from 'components/FatalError/BlockFallback';
import type { ReportErrorCallback } from 'contexts/errorReportingContext';
import { ErrorReportingContext, loggerErrorHandler } from 'contexts/errorReportingContext';
import { ComponentType, ErrorInfo, FC, ReactNode, useCallback, useContext } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

import type { ErrorBoundaryContext, ErrorBoundaryContextProps } from './errorBoundary';

export function isFailedToLoadDynamicImport(error: any): error is TypeError {
  return error instanceof TypeError && error.message.startsWith('Failed to fetch dynamically imported module');
}

export const WithFallbackContext = (
  FallBackComponent: ComponentType<FallbackProps & ErrorBoundaryContextProps>,
  context?: ErrorBoundaryContext,
): ComponentType<FallbackProps> => {
  return props => {
    return <FallBackComponent {...props} context={context} />;
  };
};

export const GenericErrorBoundary: FC<
  {
    children: ReactNode;
    Fallback?: ComponentType<FallbackProps>;
    onReset?: (...args: unknown[]) => void;
  } & ErrorBoundaryContextProps
> = ({ children, Fallback, onReset, context }) => {
  // To avoid breaking storybook/ladle, we use a context rather than introducing a dependency to redux store
  const reportError = useContext(ErrorReportingContext);

  const onError: ReportErrorCallback = useCallback(
    async (err: unknown, info: ErrorInfo, ctx: ErrorBoundaryContext | undefined) => {
      loggerErrorHandler(err, info, ctx);
      try {
        reportError(err, info, ctx);
      } catch (ex) {
        const err = await unminifyError(ex);
        getFrontLogger().error('Error while sending automatic email: {@exception} ', err);
      }
    },
    [reportError],
  );
  return (
    <ErrorBoundary
      FallbackComponent={WithFallbackContext(Fallback ?? BlockFallback, context)}
      onError={onError}
      onReset={onReset}
    >
      {children ?? null}
    </ErrorBoundary>
  );
};
